import { useTranslation } from 'react-i18next';

import { DataPanelAutoWrapInlineItem, DataPanelItem, DataPanelRow } from '@/ui';
import type { Caller } from '@/signalr';
import {
  BLOOD_TYPE_LABELS,
  formatAsListOfStrings,
  formatHeight,
  formatWeight,
  medicDataAge,
} from '@ngsos/portal-core/incidents';

type MobileAppSourceData = NonNullable<Caller['dataSources']['mobileApp'][number]['data']>;
type MobileAppMedicData = MobileAppSourceData['medicData'];

export type MobileAppMedicDataPanelItemsProps = {
  medicData: MobileAppMedicData;
  createdTimeUtc: string;
};

export function MobileAppMedicDataPanelItems({ medicData, createdTimeUtc }: MobileAppMedicDataPanelItemsProps) {
  const { t } = useTranslation();

  const conditionLabels = [
    medicData.diabetes ? t('Incident_InfoPanel_MedicData_Diabetes') : null,
    medicData.epilepsy ? t('Incident_InfoPanel_MedicData_Epilepsy') : null,
    medicData.heart ? t('Incident_InfoPanel_MedicData_HeartDefect') : null,
    medicData.lungs ? t('Incident_InfoPanel_MedicData_LungDisease') : null,
    medicData.mentalDisorders ? t('Incident_InfoPanel_MedicData_MentalDisorders') : null,
  ].filter((value): value is string => value !== null);

  const assistanceLabels = [
    medicData.hearingImpairment ? t('Incident_InfoPanel_MedicData_Hearing') : null,
    medicData.blind ? t('Incident_InfoPanel_MedicData_Blind') : null,
    medicData.speechDefect ? t('Incident_InfoPanel_MedicData_SpeechDefect') : null,
  ].filter((value): value is string => value !== null);

  return (
    <>
      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_Age')}
          value={medicDataAge(medicData.yearOfBirth, createdTimeUtc)?.toString()}
          showEmptyPlaceholder
        />
        <DataPanelItem
          title={t('Incident_InfoPanel_CallerHeight')}
          value={formatHeight(medicData.height)}
          contentCssClass="text-center"
          showEmptyPlaceholder
        />
        <DataPanelItem
          title={t('Incident_InfoPanel_CallerWeight')}
          value={formatWeight(medicData.weight)}
          contentCssClass="text-end"
          showEmptyPlaceholder
        />
      </DataPanelRow>

      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_Sex')}
          value={medicData.sex === 'Unknown' ? undefined : t(`Incident_InfoPanel_Sex_${medicData.sex}`)}
          showEmptyPlaceholder
        />
        <DataPanelItem
          title={t('Incident_InfoPanel_AppleEed_BloodType')}
          value={BLOOD_TYPE_LABELS[medicData.bloodType]}
          contentCssClass="text-end"
          showEmptyPlaceholder
        />
      </DataPanelRow>

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_MedicData')}
        value={formatAsListOfStrings(conditionLabels)}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_MedicData_Assistance')}
        value={formatAsListOfStrings(assistanceLabels)}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_RegularPrescription')}
        value={medicData.regularPrescription ?? undefined}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_AdditionalInformation')}
        value={medicData.note ?? undefined}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_TemporaryInformation')}
        value={medicData.temporaryInformation?.message ?? undefined}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_NextOfKinNote')}
        value={medicData.nextOfKinNote ?? undefined}
        showEmptyPlaceholder
      />
    </>
  );
}
